// Port of dent-cloud-service/src/services/voucher_service/codec.js.
// Kept deliberately identical: the client rejects a mistyped card before a
// round trip, so any divergence here shows up as "the scanner says no but the
// server would have said yes". Parity is pinned by tests/unit/voucherCodec.spec.ts.
// generateCode is intentionally absent — minting is server-only.

/** 31 unambiguous symbols. Excludes 0/1/I/L/O so a human reading a printed
 *  card cannot produce a valid-but-wrong code by glyph confusion. 31 is prime,
 *  which is what makes the mod-31 check character detect every single-character
 *  substitution and every adjacent transposition. */
export const ALPHABET = "23456789ABCDEFGHJKMNPQRSTUVWXYZ";
export const BODY_LENGTH = 8;
export const CHECKED_LENGTH = BODY_LENGTH + 1;

/** Position-weighted mod-31 check character over an 8-char body. */
export function checkChar(body) {
  let sum = 0;
  for (let i = 0; i < body.length; i++) {
    const value = ALPHABET.indexOf(body[i]);
    if (value < 0) return null;
    sum += (i + 2) * value;
  }
  return ALPHABET[sum % ALPHABET.length];
}

/** Uppercase and strip every non-alphanumeric character. No glyph
 *  substitution: the alphabet already excludes every ambiguous character, so
 *  0/1/I/L/O in input is by definition a typo and must fail rather than be
 *  guessed at. */
export function normalizeCode(input) {
  if (typeof input !== "string") return "";
  return input.toUpperCase().replace(/[^A-Z0-9]/g, "");
}

/** Validates the trailing 9 characters (8 body + 1 check). The prefix is not
 *  checksummed — prefixes may contain characters outside ALPHABET, and a wrong
 *  prefix simply fails the database lookup. */
export function isValidChecksum(normalized) {
  if (typeof normalized !== "string") return false;
  if (normalized.length < CHECKED_LENGTH + 1) return false;
  const tail = normalized.slice(-CHECKED_LENGTH);
  const body = tail.slice(0, BODY_LENGTH);
  for (const ch of tail) {
    if (!ALPHABET.includes(ch)) return false;
  }
  return checkChar(body) === tail[BODY_LENGTH];
}

/** Display form, one dash after the prefix. */
export function formatCode(normalized, prefixLength) {
  return `${normalized.slice(0, prefixLength)}-${normalized.slice(prefixLength)}`;
}
